import {
  ApplyArtifactFileVersionRequestArtifactType,
  ApplyArtifactFileVersionRequestFileType,
  UploadArtifactFileParamsArtifactType,
  api,
} from "@/api";
import { VidMuseEvent } from "@/common/events";
import { logger } from "@/common/logger";
import { confirmModal } from "@/component/ConfirmModal";
import {
  MediaEditorItem,
  MediaEditorModal,
} from "@/component/MediaEditorModal";
import { useToast } from "@/component/Toast/useToast";
import { ArtifactsViewerData } from "@/hooks/useArtifactsViewerData";
import { t } from "@/i18n/utils";
import { RtcTask } from "@/store/thread";
import { toolStore } from "@/store/tool";
import { voiceStore } from "@/store/voice";
import { Attachment } from "@/types/attachment";
import {
  ImageModelViewInfo,
  MediaVersion,
  ModelViewInfo,
  RegenerateParams,
  VideoModelViewInfo,
} from "@/types/mediaArtifact";
import { createGeneratingVersionsFromTaskIds } from "@/util/artifact";
import { convertFilePathToUrl } from "@/util/convertFilePathToUrl";
import { uploadAttachments } from "@/util/uploadAttachments";
import { useRouter } from "@bprogress/next/app";
import { AxiosError } from "axios";
import { useCallback, useMemo, useState } from "react";
import {
  buildImageStoryboardItems,
  buildReferenceImageItems,
  buildVideoStoryboardItems,
} from "../utils/mediaItemBuilders";
import { createModelResolver } from "../utils/modelResolver";
import { useCreditCheck } from "./useCreditCheck";

interface UseMediaEditorModalParams {
  threadId?: string;
  threadTitle: string;
  isThreadOwner: boolean;
  canEdit: boolean;
  imageModelList: ImageModelViewInfo[];
  videoModelList: VideoModelViewInfo[];
  artifacts: ArtifactsViewerData;
  rtcTasks: RtcTask[];
  manuallyAddedVersions: Record<
    MediaEditorModalType,
    Record<number, MediaVersion[]>
  >;
  selectedVersions: Record<MediaEditorModalType, Record<number, string>>;
  onVersionsApplied: (
    type: MediaEditorModalType,
    index: number,
    versions: MediaVersion[]
  ) => void;
  onVersionSelected: (
    type: MediaEditorModalType,
    index: number,
    versionId: string
  ) => void;
}

export enum MediaEditorModalType {
  ReferenceImage = "referenceImage",
  ImageStoryboard = "imageStoryboard",
  VideoStoryboard = "videoStoryboard",
}

interface DraftState {
  currentVersionId?: string;

  prompt?: string;
  dialogue?: string;
  voiceId?: string;
  model?: ModelViewInfo;
  attachmentList?: Attachment[];
}

export const useMediaEditorModal = ({
  threadId,
  threadTitle,
  isThreadOwner,
  canEdit,
  imageModelList,
  videoModelList,
  artifacts,
  rtcTasks,
  manuallyAddedVersions,
  selectedVersions,
  onVersionsApplied,
  onVersionSelected,
}: UseMediaEditorModalParams) => {
  const router = useRouter();
  const toast = useToast();
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [modalShow, setModalShow] = useState<boolean>(false);
  const [generating, setGenerating] = useState<boolean>(false);
  const [editorTitle, setEditorTitle] = useState<string>("");
  const [modelList, setModelList] = useState<ModelViewInfo[]>([]);
  const [draftState, setDraftState] = useState<DraftState>({});
  const [applyArtifactType, setApplyArtifactType] =
    useState<ApplyArtifactFileVersionRequestArtifactType | null>(null);
  const [applyFileType, setApplyFileType] =
    useState<ApplyArtifactFileVersionRequestFileType | null>(null);
  const [uploadArtifactType, setUploadArtifactType] =
    useState<UploadArtifactFileParamsArtifactType | null>(null);
  const [uploadFileType, setUploadFileType] = useState<string>("");
  const [mediaEditorModalType, setMediaEditorModalType] =
    useState<MediaEditorModalType>(MediaEditorModalType.ImageStoryboard);

  const resolveModel = useMemo(
    () => createModelResolver(imageModelList, videoModelList),
    [imageModelList, videoModelList]
  );

  const mediaEditorItems = useMemo(() => {
    if (mediaEditorModalType === MediaEditorModalType.ReferenceImage) {
      return buildReferenceImageItems(
        artifacts.referenceImage.data,
        rtcTasks,
        manuallyAddedVersions[MediaEditorModalType.ReferenceImage],
        selectedVersions[MediaEditorModalType.ReferenceImage],
        resolveModel
      );
    } else if (mediaEditorModalType === MediaEditorModalType.ImageStoryboard) {
      return buildImageStoryboardItems(
        artifacts.imageStoryboard.data,
        rtcTasks,
        manuallyAddedVersions[MediaEditorModalType.ImageStoryboard],
        selectedVersions[MediaEditorModalType.ImageStoryboard],
        resolveModel
      );
    } else if (mediaEditorModalType === MediaEditorModalType.VideoStoryboard) {
      return buildVideoStoryboardItems(
        artifacts.videoStoryboard.data,
        rtcTasks,
        manuallyAddedVersions[MediaEditorModalType.VideoStoryboard],
        selectedVersions[MediaEditorModalType.VideoStoryboard],
        resolveModel
      );
    }
    return [];
  }, [
    mediaEditorModalType,
    artifacts,
    rtcTasks,
    manuallyAddedVersions,
    selectedVersions,
    resolveModel,
  ]);

  const { checkCredits } = useCreditCheck();

  const activeMediaItem = useMemo(() => {
    const item = mediaEditorItems[currentIndex];
    if (!item) return null;

    // 如果没有 draft 变化，直接返回原对象（避免不必要的重建）
    if (Object.keys(draftState).length === 0) {
      return item;
    }

    // Merge draftState into current item
    const newItem = { ...item };
    if (newItem.current) {
      const currentVersion = newItem.current;
      const hasChanges =
        (draftState.currentVersionId &&
          draftState.currentVersionId !== currentVersion.id) ||
        ("prompt" in draftState &&
          draftState.prompt !== currentVersion.prompt) ||
        ("dialogue" in draftState &&
          draftState.dialogue !== currentVersion.dialogue) ||
        ("voiceId" in draftState &&
          draftState.voiceId !== currentVersion.voiceId) ||
        ("model" in draftState && draftState.model !== currentVersion.model) ||
        ("attachmentList" in draftState &&
          draftState.attachmentList !== currentVersion.attachmentList);

      // 只有实际有变化时才创建新对象
      if (hasChanges) {
        newItem.current = {
          ...currentVersion,
          id: draftState.currentVersionId || currentVersion.id,
          prompt:
            "prompt" in draftState
              ? (draftState.prompt as string)
              : currentVersion.prompt,
          dialogue:
            "dialogue" in draftState
              ? (draftState.dialogue as string)
              : currentVersion.dialogue,
          voiceId:
            "voiceId" in draftState
              ? (draftState.voiceId as string)
              : currentVersion.voiceId,
          model:
            "model" in draftState
              ? (draftState.model as ModelViewInfo | undefined)
              : currentVersion.model,
          attachmentList:
            "attachmentList" in draftState
              ? (draftState.attachmentList as Attachment[]) ?? []
              : currentVersion.attachmentList,
        };
      }
    }

    return newItem;
  }, [mediaEditorItems, currentIndex, draftState]);

  const handleClose = () => {
    setModalShow(false);
  };

  const showEditorModal = (
    item: MediaEditorItem,
    index: number,
    editorTitle: string,
    modelList: ModelViewInfo[],
    applyArtifactType: ApplyArtifactFileVersionRequestArtifactType,
    applyFileType: ApplyArtifactFileVersionRequestFileType,
    uploadArtifactType: UploadArtifactFileParamsArtifactType,
    uploadFileType: string,
    mediaEditorModalType: MediaEditorModalType
  ) => {
    setCurrentIndex(index);
    setDraftState({});
    setModalShow(true);
    setEditorTitle(editorTitle);
    setModelList(modelList);
    setApplyArtifactType(applyArtifactType);
    setApplyFileType(applyFileType);
    setUploadArtifactType(uploadArtifactType);
    setUploadFileType(uploadFileType);
    setMediaEditorModalType(mediaEditorModalType);
  };

  const onReferenceImageClick = (item: MediaEditorItem, index: number) => {
    showEditorModal(
      item,
      index,
      "Reference",
      imageModelList,
      ApplyArtifactFileVersionRequestArtifactType.ValueReferenceImageArtifact,
      ApplyArtifactFileVersionRequestFileType.ReferenceImage,
      UploadArtifactFileParamsArtifactType.ValueReferenceImageArtifact,
      "reference_image",
      MediaEditorModalType.ReferenceImage
    );
  };

  const onImageStoryboardClick = (item: MediaEditorItem, index: number) => {
    showEditorModal(
      item,
      index,
      "Shot",
      imageModelList,
      ApplyArtifactFileVersionRequestArtifactType.ValueShotsArtifact,
      ApplyArtifactFileVersionRequestFileType.StartFrameImage,
      UploadArtifactFileParamsArtifactType.ValueShotsArtifact,
      "start_frame_image",
      MediaEditorModalType.ImageStoryboard
    );
  };

  const onVideoStoryboardClick = (item: MediaEditorItem, index: number) => {
    showEditorModal(
      item,
      index,
      "Shot",
      videoModelList,
      ApplyArtifactFileVersionRequestArtifactType.ValueShotsArtifact,
      ApplyArtifactFileVersionRequestFileType.VideoClip,
      UploadArtifactFileParamsArtifactType.ValueShotsArtifact,
      "video_clip",
      MediaEditorModalType.VideoStoryboard
    );
  };

  const handleGenerate = async (params: RegenerateParams) => {
    if (!threadId) {
      console.error("threadId is required");
      return;
    }

    // 如果操作被禁用，直接返回
    if (!canEdit) {
      return;
    }

    // Check credits before generating
    const hasCredits = await checkCredits(
      params.model || "",
      params.count,
      params.mediaType,
      activeMediaItem?.current?.shotDuration
    );
    if (!hasCredits) {
      return;
    }

    setGenerating(true);

    try {
      // 先上传所有附件，获取 URLs
      const attachmentUrls = await uploadAttachments(
        params.attachmentList || []
      );

      // 调用对应的生成工具
      let result: { task_id_list: string[] };

      if (mediaEditorModalType === MediaEditorModalType.ImageStoryboard) {
        result = await toolStore.callGenerateImageForShotTool(threadId, {
          count: params.count,
          modelName: params.model,
          prompt: params.prompt || "",
          attachmentUrls,
          shotIndex: currentIndex + 1,
        });
      } else if (
        mediaEditorModalType === MediaEditorModalType.VideoStoryboard
      ) {
        // 判断声音类型：通过 voiceStore 查找对应的声音配置
        const voiceOption =
          params.mediaType === "avatar"
            ? voiceStore.voices.find((v) => v.value === params.voiceId)
            : null;
        const isSystemVoice = voiceOption?.type === "system";

        result = await toolStore.callGenerateVideoForShotTool(threadId, {
          count: params.count,
          modelName: params.model,
          prompt: params.prompt || "",
          dialogue: params.mediaType === "avatar" ? params.dialogue || "" : "",
          attachmentUrls,
          shotIndex: currentIndex + 1,
          // 根据声音类型传递不同参数：系统声音传 voiceId，用户上传的声音传 voicePath
          voiceId:
            params.mediaType === "avatar" && isSystemVoice
              ? params.voiceId || ""
              : "",
          voicePath:
            params.mediaType === "avatar" && !isSystemVoice
              ? params.voiceId || ""
              : "",
          generate_type: params.mediaType === "avatar" ? "avatar" : "normal",
        });
      } else if (mediaEditorModalType === MediaEditorModalType.ReferenceImage) {
        result = await toolStore.callReferenceImageTool(threadId, {
          count: params.count,
          modelName: params.model,
          prompt: params.prompt || "",
          attachmentUrls,
          index: currentIndex + 1,
        });
      } else {
        return;
      }

      // 根据返回的 task_id_list 创建 generating 版本
      const generatingVersions = createGeneratingVersionsFromTaskIds(
        result.task_id_list,
        params.prompt || "",
        attachmentUrls,
        params.model || "",
        params.mediaType === "avatar" ? params.dialogue || "" : "",
        params.mediaType === "avatar" ? params.voiceId || "" : ""
      ).map((version) => ({
        ...version,
        shotDuration: activeMediaItem?.current?.shotDuration,
        avatarAudioType: activeMediaItem?.current?.avatarAudioType,
      }));

      onVersionsApplied(mediaEditorModalType, currentIndex, generatingVersions);

      logger.event(VidMuseEvent.MediaEditorModalGenerateBtn, {
        artifact_type: mediaEditorModalType,
        media_type: params.mediaType,
        thread_id: threadId,
        current_index: currentIndex,
        prompt: params.prompt,
        attachment_count: (params.attachmentList || []).length,
        model: params.model,
        count: params.count,
      });
    } catch (e) {
      const error = e as Error;
      const errorMessage = error.message
        ? error.message
        : t("error.generateFailed");

      if ((error as AxiosError).response?.status === 400) {
        const data = (error as AxiosError).response?.data as { code?: string };
        if (data?.code === "00400") {
          try {
            await confirmModal.confirm({
              title: t("modal.outOfCredits.title", "Out of credits"),
              content: t(
                "modal.outOfCredits.content",
                "You’ve used all available credits, upgrade or purchase a credit pack to keep creating."
              ),
              continueText: t("modal.outOfCredits.upgrade", "Upgrade"),
              backText: t("common.cancel", "Cancel"),
            });

            router.push("/pricing");
          } catch {
            // User cancelled
          }
        }
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setGenerating(false);
    }
  };

  const handleRemix = () => {
    if (!canEdit) {
      return;
    }

    const current = activeMediaItem?.current;
    if (!current) {
      return;
    }

    const attachment: Attachment = {
      id: `remix-${current.id}`,
      type: "url",
      url: current.src,
      preview: current.previewSrc,
    };

    setDraftState((prev) => ({
      ...prev,
      currentVersionId: `remix-${current.id}`,
      prompt: current.prompt,
      dialogue: current.dialogue,
      voiceId: current.voiceId,
      model: current.model,
      attachmentList: [attachment],
    }));

    logger.event(VidMuseEvent.MediaEditorModalRemixBtn, {
      thread_id: threadId,
      media_type: activeMediaItem?.mediaType || "",
      current_index: currentIndex,
    });
  };

  const handleReplace = async (file: File) => {
    if (!threadId) {
      console.error("threadId is required");
      return;
    }

    if (!uploadArtifactType || !uploadFileType) {
      console.error("uploadArtifactType or uploadFileType is required");
      return;
    }

    // 如果操作被禁用，直接返回
    if (!canEdit) {
      return;
    }

    try {
      const response = await api.threads.uploadArtifactFile(
        threadId,
        {
          artifact_type: uploadArtifactType,
          data: JSON.stringify({
            index: currentIndex,
            file_type: uploadFileType,
          }),
        },
        {
          file,
        }
      );

      // 如果上传成功并返回了 file_path，创建一个临时的成功版本
      if (response.data.file_path) {
        const src = convertFilePathToUrl(response.data.file_path);

        // 创建一个临时的成功版本
        const tempVersion: MediaVersion = {
          id: response.data.file_path,
          src: src,
          previewSrc: src,
          filePath: response.data.file_path,
          source: "upload",
          prompt: "",
          attachmentList: [],
          status: "success",
          createdTime: Date.now(),
          avatarAudioType: activeMediaItem?.current?.avatarAudioType,
        };

        // 获取现有的手动版本列表，追加新版本而不是覆盖
        const existingManualVersions =
          manuallyAddedVersions[mediaEditorModalType]?.[currentIndex] || [];
        const updatedManualVersions = [...existingManualVersions, tempVersion];

        // 添加临时版本到手动版本列表中
        onVersionsApplied(
          mediaEditorModalType,
          currentIndex,
          updatedManualVersions
        );

        // 选中新上传的版本，确保 mediaEditorItems 重新计算时使用正确的版本
        onVersionSelected(mediaEditorModalType, currentIndex, tempVersion.id);

        setDraftState({
          currentVersionId: tempVersion.id,
        });

        logger.event(VidMuseEvent.MediaEditorModalReplaceBtn, {
          thread_id: threadId,
          artifact_type: uploadArtifactType,
          media_type: activeMediaItem?.mediaType || "",
          current_index: currentIndex,
        });
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to replace artifact file";
      console.error(errorMessage);
      throw error;
    }
  };

  const handleModalIndexChange = (index: number) => {
    setCurrentIndex(index);
    setDraftState({}); // Reset draft state when changing index
  };

  const handlePromptChange = useCallback((prompt: string) => {
    setDraftState((prev) => ({ ...prev, prompt }));
  }, []);

  const handleDialogueChange = useCallback((dialogue: string) => {
    setDraftState((prev) => ({ ...prev, dialogue }));
  }, []);

  const handleVoiceIdChange = useCallback((voiceId: string) => {
    setDraftState((prev) => ({ ...prev, voiceId }));
  }, []);

  const handleModelChange = useCallback((model: ModelViewInfo) => {
    setDraftState((prev) => ({ ...prev, model }));
  }, []);

  const handleSelectVersion = async (version: MediaVersion) => {
    if (!threadId) {
      console.error("threadId is required");
      return;
    }

    if (version.id === activeMediaItem?.current?.id) {
      return;
    }

    if (!applyArtifactType || !applyFileType) {
      console.error("applyArtifactType or applyFileType is required");
      return;
    }

    setDraftState({
      currentVersionId: version.id,
    });

    onVersionSelected(mediaEditorModalType, currentIndex, version.id);

    if (version.status === "success" && isThreadOwner) {
      try {
        await api.threads.applyArtifactFileVersion(threadId, {
          index: currentIndex,
          artifact_type: applyArtifactType,
          file_type: applyFileType,
          file_path: version.filePath,
        });
      } catch (error) {
        console.error("Failed to apply artifact file version:", error);
      }
    }

    logger.event(VidMuseEvent.MediaEditorModalSelectVersionBtn, {
      thread_id: threadId,
      artifact_type: applyArtifactType,
      media_type: activeMediaItem?.mediaType || "",
      current_index: currentIndex,
    });
  };

  const renderMediaEditorModal = () => {
    if (!activeMediaItem || !threadId) {
      return null;
    }

    return (
      <MediaEditorModal
        title={editorTitle}
        threadId={threadId}
        threadTitle={threadTitle}
        item={activeMediaItem}
        index={currentIndex}
        totalCount={mediaEditorItems.length}
        open={modalShow}
        generating={generating}
        isOwner={isThreadOwner}
        canEdit={canEdit}
        modelList={modelList}
        onPromptChange={handlePromptChange}
        onClose={handleClose}
        onGenerate={handleGenerate}
        onReplace={handleReplace}
        onIndexChange={handleModalIndexChange}
        onSelectVersion={handleSelectVersion}
        onDialogueChange={handleDialogueChange}
        onVoiceIdChange={handleVoiceIdChange}
        onModelChange={handleModelChange}
        onRemix={handleRemix}
      />
    );
  };

  return {
    onReferenceImageClick,
    onImageStoryboardClick,
    onVideoStoryboardClick,
    renderMediaEditorModal,
  };
};
